import { useState, useEffect } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Review, Reply } from '@/types/review';
import { useToast } from './use-toast';
import { useAuth } from '@/components/AuthProvider';
import { useStableAvatar } from '@/hooks/use-stable-avatar';

export function useReviews(bookId: number, includeAIGenerated: boolean = true, enableFetch: boolean = false) {
  const { toast } = useToast();
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;
  const { currentProfile } = useStableAvatar();
  const reviewsPerPage = 20;

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['reviews', bookId, includeAIGenerated, userId],
    queryFn: async ({ pageParam = 0 }) => {
      try {
        const from = pageParam * reviewsPerPage;
        const to = from + reviewsPerPage - 1;

        // Fetch reviews with pagination
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('book_reviews')
          .select('*')
          .eq('book_id', bookId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .range(from, to);

        if (reviewsError) {
          console.error('Error fetching reviews:', reviewsError);
          throw new Error(`Error fetching reviews: ${reviewsError.message}`);
        }

        if (!reviewsData || reviewsData.length === 0) {
          return [];
        }

        // Get unique profile IDs and user IDs from reviews
        const profileIds = [...new Set(reviewsData
          .filter((review: any) => !review.is_generated && review.profile_id)
          .map((review: any) => review.profile_id))];
        
        const userIds = [...new Set(reviewsData
          .filter((review: any) => !review.is_generated && review.user_id && !review.profile_id)
          .map((review: any) => review.user_id))];

        // Fetch profiles by profile_id (preferred method)
        let profilesData: any[] = [];
        if (profileIds.length > 0) {
          const { data: profiles, error: profilesError } = await supabase
            .from('user_profiles')
            .select('id, user_id, name, avatar_url')
            .in('id', profileIds);

          if (profilesError) {
            console.error('Error fetching profiles by id:', profilesError);
          } else {
            profilesData = profiles || [];
          }
        }

        // Fetch profiles by user_id for older reviews without profile_id
        if (userIds.length > 0) {
          const { data: profiles, error: profilesError } = await supabase
            .from('user_profiles')
            .select('id, user_id, name, avatar_url')
            .in('user_id', userIds);

          if (profilesError) {
            console.error('Error fetching profiles by user_id:', profilesError);
          } else {
            profilesData = [...profilesData, ...(profiles || [])];
          }
        }

        // Create maps for quick lookup - both by profile_id and user_id
        const profilesByIdMap = profilesData.reduce((acc, profile) => {
          acc[profile.id] = profile;
          return acc;
        }, {} as Record<string, any>);
        
        const profilesByUserIdMap = profilesData.reduce((acc, profile) => {
          acc[profile.user_id] = profile;
          return acc;
        }, {} as Record<string, any>);

        // Fetch likes count for each review
        const reviewIds = reviewsData.map(review => review.id);
        const { data: likesData, error: likesError } = await supabase
          .from('review_likes')
          .select('review_id')
          .in('review_id', reviewIds);

        if (likesError) {
          console.error('Error fetching likes:', likesError);
        }

        // Fetch user's likes if logged in
        let userLikes: string[] = [];
        if (userId) {
          const { data: userLikesData, error: userLikesError } = await supabase
            .from('review_likes')
            .select('review_id')
            .eq('user_id', userId)
            .in('review_id', reviewIds);

          if (userLikesError) {
            console.error('Error fetching user likes:', userLikesError);
          } else {
            userLikes = userLikesData?.map(like => like.review_id) || [];
          }
        }

        // Fetch replies for each review
        const { data: repliesData, error: repliesError } = await supabase
          .from('review_replies')
          .select('*')
          .in('review_id', reviewIds)
          .order('created_at', { ascending: true });

        if (repliesError) {
          console.error('Error fetching replies:', repliesError);
        }

        // Get unique user IDs from replies (excluding AI-generated ones)
        const replyUserIds = [...new Set((repliesData || [])
          .filter(reply => !reply.is_generated && reply.user_id)
          .map(reply => reply.user_id))];

        // Fetch profiles for reply users (if not already fetched)
        const newReplyUserIds = replyUserIds.filter(id => !profilesByUserIdMap[id]);
        if (newReplyUserIds.length > 0) {
          const { data: replyProfiles, error: replyProfilesError } = await supabase
            .from('user_profiles')
            .select('id, user_id, name, avatar_url')
            .in('user_id', newReplyUserIds);

          if (!replyProfilesError && replyProfiles) {
            replyProfiles.forEach(profile => {
              profilesByUserIdMap[profile.user_id] = profile;
              profilesByIdMap[profile.id] = profile;
            });
          }
        }

        // Group replies by review_id
        const repliesByReview = (repliesData || []).reduce((acc, reply) => {
          if (!acc[reply.review_id]) {
            acc[reply.review_id] = [];
          }
          
          const userProfile = reply.is_generated ? null : profilesByUserIdMap[reply.user_id];
          
          acc[reply.review_id].push({
            id: reply.id,
            review_id: reply.review_id,
            user_id: reply.user_id,
            content: reply.content,
            created_at: reply.created_at,
            is_generated: reply.is_generated,
            user_display_name: reply.is_generated ? 'Reader' : userProfile?.name,
            user_avatar_url: reply.is_generated ? null : userProfile?.avatar_url,
          } as Reply);
          return acc;
        }, {} as Record<string, Reply[]>);

        // Count likes by review
        const likesByReview = (likesData || []).reduce((acc, like) => {
          acc[like.review_id] = (acc[like.review_id] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        // Map reviews to the correct format
        const mappedReviews: Review[] = reviewsData.map((review: any) => {
          // Get profile by profile_id first, then fall back to user_id
          const userProfile = review.is_generated 
            ? null 
            : (profilesByIdMap[review.profile_id] || profilesByUserIdMap[review.user_id]);

          // Display name: If anonymous or AI -> anonymous name; Otherwise -> profile name
          const displayName = (review.is_anonymous || review.is_generated)
            ? (review.anonymous_display_name || 'Anonymous Reader')
            : (userProfile?.name || 'Reader');

          // Avatar: Always prefer stored avatar; fallback to profile avatar
          const avatarUrl = review.user_avatar_url || userProfile?.avatar_url || null;

          return {
            id: review.id,
            book_id: review.book_id,
            user_id: review.user_id,
            profile_id: review.profile_id,
            content: review.content,
            rating: review.rating,
            created_at: review.created_at,
            updated_at: review.updated_at,
            deleted_at: review.deleted_at,
            is_generated: review.is_generated,
            is_anonymous: review.is_anonymous,
            anonymous_display_name: review.anonymous_display_name,
            user_display_name: displayName,
            user_avatar_url: avatarUrl,
            likes_count: likesByReview[review.id] || 0,
            has_user_liked: userLikes.includes(review.id),
            replies: repliesByReview[review.id] || [],
          };
        });

        // Filter out AI-generated reviews if includeAIGenerated is false
        if (!includeAIGenerated) {
          return mappedReviews.filter(review => !review.is_generated);
        }

        return mappedReviews;

      } catch (error) {
        console.error('Error in reviews query:', error);
        throw error;
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length < reviewsPerPage) {
        return undefined;
      }
      return allPages.length;
    },
    enabled: !!bookId && enableFetch, // Only fetch when explicitly enabled
    staleTime: 10 * 1000,
  });

  const reviews = data?.pages.flatMap(page => page) ?? [];

  const loadMoreReviews = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Add a manual refresh function that the user can call
  const refreshReviews = () => {
    refetch();
  };

  // Fetch the total count of reviews separately
  const { data: totalCount } = useQuery({
    queryKey: ['reviewsCount', bookId, includeAIGenerated],
    queryFn: async () => {
      try {
        let query = supabase
          .from('book_reviews')
          .select('*', { count: 'exact', head: true })
          .eq('book_id', bookId)
          .is('deleted_at', null);

        if (!includeAIGenerated) {
          query = query.eq('is_generated', false);
        }

        const { count, error } = await query;

        if (error) {
          console.error('Error fetching reviews count:', error);
          throw error;
        }

        return count || 0;
      } catch (error) {
        console.error('Error in reviews count query:', error);
        return 0;
      }
    },
    enabled: !!bookId,
    staleTime: 10 * 1000,
  });

  // Check if user has completed the book - with enhanced checking
  const { data: hasCompletedBook, isLoading: isCheckingCompletion, refetch: refetchCompletionStatus } = useQuery({
    queryKey: ['bookCompletion', bookId, userId, currentProfile?.id],
    queryFn: async () => {
      if (!userId || !currentProfile?.id) return false;

      try {
        // Fall back to checking reading sessions directly (profile-aware)
        const { data: sessionsData, error: sessionsError } = await supabase
          .from('reading_sessions')
          .select('*')
          .eq('user_id', userId)
          .eq('profile_id', currentProfile.id)
          .eq('book_id', parseInt(bookId.toString()))
          .order('end_time', { ascending: false })
          .limit(1);
          
        if (sessionsError) {
          console.error('Error checking reading sessions:', sessionsError);
          return false;
        }

        if (sessionsData && sessionsData.length > 0) {
          // Consider book completed if there's any reading session with a current_page > 0
          return sessionsData[0].current_page > 0;
        }
        
        return false;
      } catch (error) {
        console.error('Error checking book completion:', error);
        return false;
      }
    },
    enabled: !!bookId && !!userId && !!currentProfile?.id,
  });

  // Force a refetch of completion status when leaving a review fails
  const [forceRefetchCompletion, setForceRefetchCompletion] = useState(false);
  
  useEffect(() => {
    if (forceRefetchCompletion) {
      refetchCompletionStatus();
      setForceRefetchCompletion(false);
    }
  }, [forceRefetchCompletion, refetchCompletionStatus]);

  // Check if user has already left a review
  const { data: userReview } = useQuery({
    queryKey: ['userReview', bookId, userId, currentProfile?.id],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase
        .from('book_reviews')
        .select('*')
        .eq('book_id', bookId)
        .eq('user_id', userId)
        .is('deleted_at', null)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user review:', error);
        return null;
      }

      if (!data) return null;

      // Fetch the profile by profile_id if available, otherwise by user_id
      let profileData = null;
      if (data.profile_id) {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('id, user_id, name, avatar_url')
          .eq('id', data.profile_id)
          .maybeSingle();

        if (profileError) {
          console.error('Error fetching profile by id:', profileError);
        } else {
          profileData = profile;
        }
      }

      // Fallback to fetching by user_id if no profile found
      if (!profileData) {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('id, user_id, name, avatar_url')
          .eq('user_id', userId)
          .eq('is_primary', true)
          .maybeSingle();

        if (profileError) {
          console.error('Error fetching user profile:', profileError);
        } else {
          profileData = profile;
        }
      }

      // Display name: If anonymous -> anonymous name; Otherwise -> profile name
      const displayName = data.is_anonymous
        ? (data.anonymous_display_name || 'Anonymous Reader')
        : (profileData?.name || 'Reader');

      // Avatar: Prefer stored avatar; otherwise profile avatar
      const avatarUrl = data.user_avatar_url || profileData?.avatar_url || null;

      // Map the review with profile data
      return {
        ...data,
        user_display_name: displayName,
        user_avatar_url: avatarUrl,
        likes_count: 0,
        has_user_liked: false,
        replies: [],
      } as Review;
    },
    enabled: !!bookId && !!userId,
  });

  // Add a new review
  const addReviewMutation = useMutation({
    mutationFn: async ({ content, rating, isAnonymous }: { content: string; rating: number; isAnonymous?: boolean }) => {
      if (!userId) {
        throw new Error('User must be logged in to leave a review');
      }

      if (!hasCompletedBook) {
        // This will trigger a refetch of the completion status
        setForceRefetchCompletion(true);
        throw new Error('You must complete the book before leaving a review');
      }

      try {
        // Generate anonymous display name if posting anonymously
        let anonymousDisplayName = null;
        const postAsAnonymous = isAnonymous ?? currentProfile?.profile_settings?.anonymous_mode;
        
        if (postAsAnonymous) {
          // Generate a unique anonymous display name
          const adjectives = ['Happy', 'Curious', 'Thoughtful', 'Avid', 'Passionate', 'Wise', 'Creative'];
          const nouns = ['Reader', 'Bookworm', 'Scholar', 'Explorer', 'Enthusiast', 'Learner', 'Storyteller'];
          const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
          const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
          const randomNumber = Math.floor(Math.random() * 999) + 1;
          anonymousDisplayName = `${randomAdjective} ${randomNoun} ${randomNumber}`;
        }

        const { data, error } = await supabase
          .from('book_reviews')
          .insert({
            user_id: userId,
            profile_id: currentProfile?.id,
            book_id: bookId,
            content,
            rating,
            is_anonymous: !!postAsAnonymous,
            anonymous_display_name: anonymousDisplayName,
            user_avatar_url: currentProfile?.avatar_url,
          })
          .select()
          .single();

        if (error) {
          throw new Error(`Error adding review: ${error.message}`);
        }

        return data;
      } catch (error) {
        console.error('Error adding review:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', bookId], exact: false });
      queryClient.invalidateQueries({ queryKey: ['userReview', bookId, userId] });
      toast({
        title: 'Review added',
        description: 'Your review has been added successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update an existing review
  const updateReviewMutation = useMutation({
    mutationFn: async ({ reviewId, content, rating, isAnonymous }: { reviewId: string; content: string; rating: number; isAnonymous?: boolean }) => {
      if (!userId) {
        throw new Error('User must be logged in to update a review');
      }

      try {
        // Generate or clear anonymous display name based on isAnonymous toggle
        let anonymousDisplayName = null;
        
        if (isAnonymous) {
          // Generate a unique anonymous display name
          const adjectives = ['Happy', 'Curious', 'Thoughtful', 'Avid', 'Passionate', 'Wise', 'Creative'];
          const nouns = ['Reader', 'Bookworm', 'Scholar', 'Explorer', 'Enthusiast', 'Learner', 'Storyteller'];
          const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)];
          const randomNoun = nouns[Math.floor(Math.random() * nouns.length)];
          const randomNumber = Math.floor(Math.random() * 999) + 1;
          anonymousDisplayName = `${randomAdjective} ${randomNoun} ${randomNumber}`;
        }

        const { data, error } = await supabase
          .from('book_reviews')
          .update({
            content,
            rating,
            is_anonymous: !!isAnonymous,
            anonymous_display_name: anonymousDisplayName,
            user_avatar_url: currentProfile?.avatar_url || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', reviewId)
          .eq('user_id', userId)
          .select()
          .single();

        if (error) {
          throw new Error(`Error updating review: ${error.message}`);
        }

        return data;
      } catch (error) {
        console.error('Error updating review:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', bookId], exact: false });
      queryClient.invalidateQueries({ queryKey: ['userReview', bookId, userId] });
      toast({
        title: 'Review updated',
        description: 'Your review has been updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete a review (soft delete)
  const deleteReviewMutation = useMutation({
    mutationFn: async (reviewId: string) => {
      if (!userId) {
        throw new Error('User must be logged in to delete a review');
      }

      try {
        // Check if user is admin using user_roles table (matches RLS policy)
        const { data: adminData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .eq('role', 'admin')
          .maybeSingle();

        const isAdmin = !!adminData;

        // Use admin function if admin, otherwise regular update
        if (isAdmin) {
          const { error } = await supabase.rpc('admin_delete_review', {
            review_id: reviewId
          });

          if (error) {
            throw new Error(`Error deleting review: ${error.message}`);
          }
        } else {
          // Regular user soft delete (their own review)
          const { error } = await supabase
            .from('book_reviews')
            .update({
              deleted_at: new Date().toISOString(),
            })
            .eq('id', reviewId)
            .eq('user_id', userId);

          if (error) {
            throw new Error(`Error deleting review: ${error.message}`);
          }
        }

        return data;
      } catch (error) {
        console.error('Error deleting review:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', bookId], exact: false });
      queryClient.invalidateQueries({ queryKey: ['userReview', bookId, userId] });
      toast({
        title: 'Review deleted',
        description: 'Your review has been deleted',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Like a review
  const likeReviewMutation = useMutation({
    mutationFn: async (reviewId: string) => {
      if (!userId) {
        throw new Error('User must be logged in to like a review');
      }

      try {
        // First check if user already liked this review
        const { data: existingLikes, error: checkError } = await supabase
          .from('review_likes')
          .select('id')
          .eq('user_id', userId)
          .eq('review_id', reviewId)
          .maybeSingle();

        if (checkError) {
          throw new Error(`Error checking like status: ${checkError.message}`);
        }

        if (existingLikes) {
          // Unlike if already liked
          const { error } = await supabase
            .from('review_likes')
            .delete()
            .eq('user_id', userId)
            .eq('review_id', reviewId);

          if (error) {
            throw new Error(`Error unliking review: ${error.message}`);
          }
          return { action: 'unliked', reviewId };
        } else {
          // Like if not already liked
          const { error } = await supabase
            .from('review_likes')
            .insert({
              user_id: userId,
              review_id: reviewId
            });

          if (error) {
            throw new Error(`Error liking review: ${error.message}`);
          }
          return { action: 'liked', reviewId };
        }
      } catch (error) {
        console.error('Error liking/unliking review:', error);
        throw error;
      }
    },
    onMutate: async (reviewId: string) => {
      await queryClient.cancelQueries({ queryKey: ['reviews', bookId, includeAIGenerated, userId] });
      const previousReviews = queryClient.getQueryData<Review[]>(['reviews', bookId, includeAIGenerated, userId]);

      if (previousReviews) {
        queryClient.setQueryData<Review[]>(['reviews', bookId, includeAIGenerated, userId], (oldReviews) => {
          if (!oldReviews) return [];
          return oldReviews.map(review => {
            if (review.id === reviewId) {
              const alreadyLiked = review.has_user_liked;
              return {
                ...review,
                has_user_liked: !alreadyLiked,
                likes_count: alreadyLiked ? review.likes_count - 1 : review.likes_count + 1,
              };
            }
            return review;
          });
        });
      }
      return { previousReviews };
    },
    onError: (error, reviewId, context) => {
      if (context?.previousReviews) {
        queryClient.setQueryData(['reviews', bookId, includeAIGenerated, userId], context.previousReviews);
      }
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', bookId, includeAIGenerated, userId] });
    },
  });

  // Add a reply to a review
  const addReplyMutation = useMutation({
    mutationFn: async ({ reviewId, content }: { reviewId: string; content: string }) => {
      if (!userId) {
        throw new Error('User must be logged in to reply');
      }

      try {
        // Insert the reply directly 
        const { data, error } = await supabase
          .from('review_replies')
          .insert({
            user_id: userId,
            review_id: reviewId,
            content: content,
            is_generated: false,
            anonymous_display_name: null
          })
          .select();

        if (error) {
          throw new Error(`Error adding reply: ${error.message}`);
        }

        return data;
      } catch (error) {
        console.error('Error adding reply:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', bookId], exact: false });
      toast({
        title: 'Reply added',
        description: 'Your reply has been added successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete a reply
  const deleteReplyMutation = useMutation({
    mutationFn: async (replyId: string) => {
      if (!userId) {
        throw new Error('User must be logged in to delete a reply');
      }

      try {
        // Check if user is admin using user_roles table (matches RLS policy)
        const { data: adminData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .eq('role', 'admin')
          .maybeSingle();

        const isAdmin = !!adminData;

        // Perform the delete - RLS will handle the authorization
        const { error } = await supabase
          .from('review_replies')
          .delete()
          .eq('id', replyId);

        if (error) {
          throw new Error(`Error deleting reply: ${error.message}`);
        }

        return replyId;
      } catch (error) {
        console.error('Error deleting reply:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', bookId], exact: false });
      toast({
        title: 'Reply deleted',
        description: 'Your reply has been deleted',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    reviews,
    isLoading,
    error,
    refreshReviews,
    hasCompletedBook,
    isCheckingCompletion,
    userReview,
    totalCount,
    loadMoreReviews,
    hasNextPage,
    isFetchingNextPage,
    addReview: addReviewMutation.mutate,
    updateReview: updateReviewMutation.mutate,
    deleteReview: deleteReviewMutation.mutate,
    likeReview: likeReviewMutation.mutate,
    addReply: addReplyMutation.mutate,
    deleteReply: deleteReplyMutation.mutate,
    isAddingReview: addReviewMutation.isPending,
    isUpdatingReview: updateReviewMutation.isPending,
    isDeletingReview: deleteReviewMutation.isPending,
    isLikingReview: likeReviewMutation.isPending,
    isAddingReply: addReplyMutation.isPending,
    isDeletingReply: deleteReplyMutation.isPending,
  };
}
